<div class="ie-root">
  <header class="ie-topbar">
    <button type="button" class="ie-icon-btn" (click)="cancel()" matTooltip="Close without saving"><mat-icon>close</mat-icon></button>
    <span class="ie-title">{{ title }}</span>
    <span class="ie-spacer"></span>
    <button type="button" class="ie-icon-btn" [disabled]="!canUndo()" (click)="undo()" matTooltip="Undo (Ctrl+Z)"><mat-icon>undo</mat-icon></button>
    <button type="button" class="ie-icon-btn" [disabled]="!canRedo()" (click)="redo()" matTooltip="Redo (Ctrl+Y)"><mat-icon>redo</mat-icon></button>
    <button type="button" mat-flat-button class="ie-save" [disabled]="!isDirty() || saving()" (click)="save(saveAsCopyByDefault())">
      {{ saveAsCopyByDefault() ? 'Save as copy' : 'Save' }}
    </button>
    <button type="button" class="ie-icon-btn" [matMenuTriggerFor]="moreMenu" matTooltip="More"><mat-icon>more_vert</mat-icon></button>
    <mat-menu #moreMenu="matMenu" xPosition="before">
      <button mat-menu-item [disabled]="!isDirty() || saving()" (click)="save(!saveAsCopyByDefault())">
        <mat-icon>{{ saveAsCopyByDefault() ? 'save' : 'library_add' }}</mat-icon>
        <span>{{ saveAsCopyByDefault() ? 'Replace original' : 'Save as copy' }}</span>
      </button>
      <button mat-menu-item [disabled]="!isDirty()" (click)="revert()">
        <mat-icon>restart_alt</mat-icon>
        <span>Revert to original</span>
      </button>
    </mat-menu>
  </header>

  <div class="ie-stage" #stage>
    <canvas #canvas class="ie-canvas" (pointerdown)="onPointerDown($event)" (pointermove)="onPointerMove($event)"
      (pointerup)="onPointerUp($event)" (pointercancel)="onPointerUp($event, true)" (wheel)="onWheel($event)"></canvas>
    @if (loading()) {
      <div class="ie-stage-message"><mat-spinner diameter="40"></mat-spinner></div>
    } @else if (loadError()) {
      <div class="ie-stage-message">This image could not be loaded.</div>
    } @else {
      <!-- zoom: also two fingers on touch screens, the mouse wheel, space + drag to move -->
      <div class="ie-zoom">
        <button type="button" class="ie-icon-btn" [disabled]="zoom() <= 1" (click)="zoomOut()" matTooltip="Zoom out"><mat-icon>remove</mat-icon></button>
        <button type="button" class="ie-zoom-value" [disabled]="zoom() <= 1" (click)="fitView()" matTooltip="Fit to screen">{{ (zoom() * 100).toFixed(0) }}%</button>
        <button type="button" class="ie-icon-btn" [disabled]="zoom() >= 8" (click)="zoomIn()" matTooltip="Zoom in (or two fingers / mouse wheel)"><mat-icon>add</mat-icon></button>
      </div>
    }
    <!-- always in the DOM (hidden when not typing), so it can be focused synchronously within a tap (iOS keyboard) -->
    <input #textInput class="ie-text-input" [class.ie-text-active]="!!textBox()" type="text" aria-label="Text, Enter to place"
      autocomplete="off" enterkeyhint="done" [attr.tabindex]="textBox() ? null : -1"
      [style.left.px]="textBox()?.left ?? 0" [style.top.px]="textBox()?.top ?? 0" [style.width.px]="textBox()?.width ?? 0"
      [style.transform]="textBox()?.transform ?? null" [style.color]="textBox()?.color ?? color()"
      (input)="layoutText(textInput.value)" (keydown)="onTextKeydown($event)" (blur)="commitText()">
  </div>

  <div class="ie-options">
    @if (tool() === 'crop') {
      @if (listingFrameEnabled) {
        <div class="ie-group">
          @for (t of cropTargets; track t.target) {
            <button type="button" class="ie-chip" [class.ie-selected]="cropTarget() === t.target" (click)="setCropTarget(t.target)" [matTooltip]="t.tooltip">{{ t.label }}</button>
          }
        </div>
      }
      @if (cropTarget() === 'photo') {
        <div class="ie-group">
          @for (a of aspects; track a.mode) {
            <button type="button" class="ie-chip" [class.ie-selected]="aspect() === a.mode" (click)="setAspect(a.mode)" [matTooltip]="a.tooltip">{{ a.label }}</button>
          }
        </div>
        <div class="ie-group">
          <button type="button" class="ie-icon-btn" (click)="rotate(false)" matTooltip="Rotate left"><mat-icon>rotate_left</mat-icon></button>
          <button type="button" class="ie-icon-btn" (click)="rotate(true)" matTooltip="Rotate right"><mat-icon>rotate_right</mat-icon></button>
          <button type="button" class="ie-icon-btn" (click)="flip(true)" matTooltip="Flip horizontal"><mat-icon>flip</mat-icon></button>
          <button type="button" class="ie-icon-btn" (click)="flip(false)" matTooltip="Flip vertical"><mat-icon class="ie-rotate90">flip</mat-icon></button>
        </div>
      } @else {
        <div class="ie-group">
          @for (f of fits; track f.fit) {
            <button type="button" class="ie-chip" [class.ie-selected]="state()?.fit === f.fit" (click)="setFit(f.fit)" [matTooltip]="f.tooltip">{{ f.label }}</button>
          }
          <button type="button" class="ie-icon-btn" [disabled]="!state()?.listingFrame" (click)="resetFrame()" matTooltip="Reset frame (whole photo centered)"><mat-icon>fit_screen</mat-icon></button>
        </div>
      }
    } @else {
      <div class="ie-group">
        @for (t of markupTools; track t.tool) {
          <button type="button" class="ie-icon-btn" [class.ie-selected]="markupTool() === t.tool" (click)="setMarkupTool(t.tool)" [matTooltip]="t.label"><mat-icon>{{ t.icon }}</mat-icon></button>
        }
      </div>
      <div class="ie-group">
        @for (c of colors; track c) {
          <button type="button" class="ie-swatch" [class.ie-selected]="(selectedAnnotation()?.color ?? color()) === c" [style.background]="c" (click)="setColor(c)" [attr.aria-label]="'Color ' + c"></button>
        }
      </div>
      <div class="ie-group">
        @for (s of sizes; track s) {
          <button type="button" class="ie-chip" [class.ie-selected]="size() === s" (click)="setSize(s)" matTooltip="Size">{{ s }}</button>
        }
        @if (selectedAnnotation(); as selected) {
          @if (selected.kind === 'text') {
            <button type="button" class="ie-icon-btn" (click)="editSelectedText()" matTooltip="Edit text"><mat-icon>edit</mat-icon></button>
          }
          <button type="button" class="ie-icon-btn" (click)="deleteSelected()" matTooltip="Delete selected markup (Del)"><mat-icon>delete</mat-icon></button>
        } @else {
          <button type="button" class="ie-icon-btn" [disabled]="!hasMarkup()" (click)="clearMarkup()" matTooltip="Remove all markup"><mat-icon>layers_clear</mat-icon></button>
        }
      </div>
    }
  </div>

  <nav class="ie-tools">
    <button type="button" class="ie-tool" [class.ie-selected]="tool() === 'crop'" (click)="setTool('crop')">
      <mat-icon>crop_rotate</mat-icon><span>Crop &amp; rotate</span>
    </button>
    <button type="button" class="ie-tool" [class.ie-selected]="tool() === 'markup'" (click)="setTool('markup')">
      <mat-icon>draw</mat-icon><span>Markup</span>
    </button>
  </nav>
</div>
